import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Alert, AuthCard, Button, Spinner } from '@sentinel/ui';
import { useSession } from '@sentinel/auth-core';
import { useLaunchProducts } from '../features/products/use-products';
import { ProductCard } from '../features/products/components/product-card';
import { resolveProductDestination } from '../features/products/resolve-launch-target';

const styles = stylex.create({
  list: { display: 'grid', gap: '0.5rem', margin: 0, padding: 0, listStyle: 'none' },
  footer: { marginTop: '1rem', textAlign: 'center' },
  retry: { marginTop: '0.75rem' },
});

export function SelectProductPage() {
  const { t } = useTranslation('products');
  const { user, loading } = useSession();
  const navigate = useNavigate();
  const { items, total, isLoading, isError, refetch } = useLaunchProducts(!loading && !!user);

  useEffect(() => {
    if (loading || user) return;
    void navigate({ to: '/login', replace: true });
  }, [user, loading, navigate]);

  // Re-apply the same routing decision as /launch so a direct visit (bookmark)
  // with 0 or 1 product behaves identically; only >1 renders the chooser.
  useEffect(() => {
    if (loading || !user || isLoading || isError) return;
    const dest = resolveProductDestination({ isError, total, items });
    if (dest.kind === 'external') {
      window.location.assign(dest.url);
      return;
    }
    if (dest.kind === 'account') {
      void navigate({ to: '/account', replace: true });
    }
    // chooser → render below
  }, [loading, user, isLoading, isError, total, items, navigate]);

  return (
    <AuthCard testId="select-product" title={t('select_title')} subtitle={t('select_subtitle')}>
      {isLoading ? (
        <Spinner label={t('loading')} />
      ) : isError ? (
        <>
          <Alert type="error" role="alert">{t('load_error')}</Alert>
          <div {...stylex.props(styles.retry)}>
            <Button type="button" variant="secondary" data-testid="select-product-retry" onClick={() => refetch()}>
              {t('retry')}
            </Button>
          </div>
          <p {...stylex.props(styles.footer)}>
            <Link to="/account" data-testid="select-product-manage-account">{t('manage_account')}</Link>
          </p>
        </>
      ) : total > 1 ? (
        <>
          <ul {...stylex.props(styles.list)}>
            {items.map((p) => (
              <li key={p.product_id}><ProductCard product={p} /></li>
            ))}
          </ul>
          <p {...stylex.props(styles.footer)}>
            <Link to="/account" data-testid="select-product-manage-account">{t('manage_account')}</Link>
          </p>
        </>
      ) : (
        <Spinner label={t('resolving')} />
      )}
    </AuthCard>
  );
}

export const Route = createFileRoute('/select-product')({
  component: SelectProductPage,
});
